type CbarDateInputElement = HTMLElement & {
    date: Date | null;
    minDate: Date | null;
    maxDate: Date | null;
    value?: string;
    open?: boolean;
    fixedMenuPosition?: boolean;
};

const CBAR_DATE_SUBMIT_NAME_ATTR = 'data-cbar-date-submit-name';
const CBAR_DATE_HIDDEN_ATTR = 'data-cbar-date-field';
const CBAR_DATE_HYDRATED_ATTR = 'data-cbar-date-hydrated';
const LOCAL_YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parse YYYY-MM-DD as a local calendar date (PHP filter values). */
export function parseLocalYmd(text: string | null | undefined): Date | null {
    const trimmed = text?.trim() ?? '';
    if (!trimmed) {
        return null;
    }

    const match = trimmed.match(LOCAL_YMD_RE);
    if (!match) {
        return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsed = new Date(year, month - 1, day);

    if (
        parsed.getFullYear() !== year
        || parsed.getMonth() !== month - 1
        || parsed.getDate() !== day
    ) {
        return null;
    }

    return parsed;
}

/** Parse YYYY-MM-DD, ISO-8601, or other date strings as a local calendar date. */
export function parseFormDateText(text: string | null | undefined): Date | null {
    const trimmed = text?.trim() ?? '';
    if (!trimmed) {
        return null;
    }

    const local = parseLocalYmd(trimmed);
    if (local) {
        return local;
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    return calendarDateFromDate(parsed);
}

function calendarDateFromDate(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseDateBound(value: unknown): Date | null {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value;
    }

    if (typeof value === 'string') {
        return parseFormDateText(value);
    }

    return null;
}

function readInitialDateText(element: CbarDateInputElement): string {
    const attrValue = element.getAttribute('value')?.trim() ?? '';
    if (attrValue) {
        return attrValue;
    }

    return element.value?.trim() ?? '';
}

/** Resolve the committed date, including server-rendered value= before hydration. */
export function resolveCbarDateInputDate(
    element: CbarDateInputElement | null | undefined
): Date | null {
    if (!element) {
        return null;
    }

    if (element.date) {
        return calendarDateFromDate(element.date);
    }

    if (element.hasAttribute(CBAR_DATE_HYDRATED_ATTR)) {
        return null;
    }

    const text = readInitialDateText(element);
    if (!text) {
        return null;
    }

    return parseFormDateText(text);
}

/** Local calendar date as YYYY-MM-DD (PHP / Zend_Validate_Date compatible). */
export function formatLocalYmd(date: Date | null | undefined): string {
    if (!date) {
        return '';
    }

    const local = calendarDateFromDate(date);
    const year = local.getFullYear();
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const day = String(local.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function isCbarDateInput(
    element: Element | null | undefined
): element is CbarDateInputElement {
    return element?.tagName === 'CBAR-DATE-INPUT';
}

/** Value to send in query strings / POST fields for a cbar-date-input. */
export function getCbarDateInputSubmitValue(
    element: CbarDateInputElement | null | undefined
): string {
    return formatLocalYmd(resolveCbarDateInputDate(element));
}

function hydrateDateBound(
    element: CbarDateInputElement,
    attribute: 'min-date' | 'max-date',
    property: 'minDate' | 'maxDate'
): void {
    if (parseDateBound(element[property])) {
        return;
    }

    const parsed = parseFormDateText(element.getAttribute(attribute));
    if (parsed) {
        element[property] = parsed;
    }
}

/**
 * Copy server-rendered value="Y-m-d" (and min/max bounds) into the
 * cbar-date-input date properties so filters and FormData round-trip.
 */
export function hydrateCbarDateInput(element: CbarDateInputElement): void {
    if (element.hasAttribute(CBAR_DATE_HYDRATED_ATTR)) {
        return;
    }

    if (!element.date) {
        const parsed = resolveCbarDateInputDate(element);
        if (parsed) {
            element.date = parsed;
            const ymd = formatLocalYmd(parsed);
            if (ymd) {
                element.setAttribute('value', ymd);
            }
        }
    }

    hydrateDateBound(element, 'min-date', 'minDate');
    hydrateDateBound(element, 'max-date', 'maxDate');

    element.setAttribute(CBAR_DATE_HYDRATED_ATTR, '');
}

export function hydrateCbarDateInputsIn(root: ParentNode = document): void {
    root.querySelectorAll('cbar-date-input').forEach((node) => {
        if (isCbarDateInput(node)) {
            hydrateCbarDateInput(node);
        }
    });
}

export async function setupCbarDateInputHydration(): Promise<void> {
    await customElements.whenDefined('cbar-date-input');

    const run = () => {
        hydrateCbarDateInputsIn(document);
        setupExclusiveDatePopovers(document);
    };
    run();

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, {once: true});
    }

    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => {
                if (!(node instanceof HTMLElement)) {
                    return;
                }

                if (isCbarDateInput(node)) {
                    hydrateCbarDateInput(node);
                    setupExclusiveDatePopovers(node.parentElement ?? document);
                    return;
                }

                hydrateCbarDateInputsIn(node);
                setupExclusiveDatePopovers(node);
            });
        }
    });

    observer.observe(document.documentElement, {childList: true, subtree: true});
}

function hiddenInputForDateField(
    form: HTMLFormElement,
    name: string
): HTMLInputElement {
    const selector = `input[type="hidden"][${CBAR_DATE_HIDDEN_ATTR}="${CSS.escape(name)}"]`;
    let hidden = form.querySelector(selector) as HTMLInputElement | null;
    if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.setAttribute(CBAR_DATE_HIDDEN_ATTR, name);
        form.appendChild(hidden);
    }

    return hidden;
}

/**
 * Replace ISO form values from cbar-date-input with local YYYY-MM-DD hidden
 * inputs before the browser (or FormData) serializes the form.
 */
export function applyCbarDateInputSubmitValues(form: HTMLFormElement): void {
    form.querySelectorAll('cbar-date-input[name]').forEach((node) => {
        const input = node as CbarDateInputElement;
        const name = input.getAttribute('name');
        if (!name) {
            return;
        }

        const hidden = hiddenInputForDateField(form, name);
        hidden.name = name;
        hidden.value = getCbarDateInputSubmitValue(input);

        if (!input.hasAttribute(CBAR_DATE_SUBMIT_NAME_ATTR)) {
            input.setAttribute(CBAR_DATE_SUBMIT_NAME_ATTR, name);
        }
        input.removeAttribute('name');
    });
}

/** Restore cbar-date-input name attributes after submit / FormData capture. */
export function restoreCbarDateInputNames(form: HTMLFormElement): void {
    form.querySelectorAll(`cbar-date-input[${CBAR_DATE_SUBMIT_NAME_ATTR}]`).forEach((node) => {
        const input = node as CbarDateInputElement;
        const name = input.getAttribute(CBAR_DATE_SUBMIT_NAME_ATTR);
        if (name) {
            input.setAttribute('name', name);
            input.removeAttribute(CBAR_DATE_SUBMIT_NAME_ATTR);
        }
    });
}

function sameCalendarDay(
    a: Date | null | undefined,
    b: Date | null | undefined
): boolean {
    if (a == null && b == null) {
        return true;
    }
    if (a == null || b == null) {
        return false;
    }
    return a.getTime() === b.getTime();
}

/** Apply from/to min/max constraints without redundant property writes. */
export function applyDateRangeConstraints(
    fromInput: CbarDateInputElement,
    toInput: CbarDateInputElement
): void {
    const fromDate = fromInput.date ?? null;
    const toDate = toInput.date ?? null;

    if (!sameCalendarDay(toInput.minDate, fromDate)) {
        toInput.minDate = fromDate;
    }
    if (!sameCalendarDay(fromInput.maxDate, toDate)) {
        fromInput.maxDate = toDate;
    }
}

/** True while a date field (or its calendar popover) is being used. */
export function isDateInputBusy(...fields: CbarDateInputElement[]): boolean {
    return fields.some(
        (field) => field.open === true || field.matches(':focus-within')
    );
}

let datePopoverDismissWired = false;

type CbarMenuElement = HTMLElement & { open?: boolean };

function pointInClientRect(x: number, y: number, rect: DOMRect): boolean {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

function findDateInputsUnderPointer(x: number, y: number): CbarDateInputElement[] {
    const hits: CbarDateInputElement[] = [];

    document.querySelectorAll('cbar-date-input').forEach((node) => {
        if (!isCbarDateInput(node)) {
            return;
        }

        const rect = node.getBoundingClientRect();
        if (pointInClientRect(x, y, rect)) {
            hits.push(node);
        }
    });

    return hits;
}

/** Prefer the closed sibling when a calendar popover overlaps another field. */
function resolveTargetDateInputFromPointer(
    x: number,
    y: number
): CbarDateInputElement | null {
    const hits = findDateInputsUnderPointer(x, y);
    if (hits.length === 0) {
        return null;
    }

    return hits.find((input) => !input.open) ?? hits[hits.length - 1];
}

function forceCloseDatePopover(input: CbarDateInputElement): void {
    input.open = false;

    const menu = input.shadowRoot?.querySelector('cbar-menu') as CbarMenuElement | null;
    if (menu?.open) {
        menu.open = false;
    }
}

function closeOpenDatePopoversExcept(active: CbarDateInputElement | null): void {
    document.querySelectorAll('cbar-date-input').forEach((node) => {
        if (!isCbarDateInput(node) || node === active || !node.open) {
            return;
        }
        forceCloseDatePopover(node);
    });
}

function isPointerInsideOpenDateMenu(
    path: EventTarget[],
    input: CbarDateInputElement
): boolean {
    const menu = input.shadowRoot?.querySelector('cbar-menu');
    if (menu && path.includes(menu)) {
        return true;
    }

    // Fallback when the surface node is retargeted but the picker remains in-path.
    const picker = input.shadowRoot?.querySelector('cbar-date-picker');
    return !!picker && path.includes(picker);
}

/**
 * Prefer the open calendar whose menu contains the event path.
 * Geometric sibling hit-testing must not steal clicks on month/year controls
 * when the popover overlaps another cbar-date-input below.
 */
function findOpenDateInputOwningPointer(path: EventTarget[]): CbarDateInputElement | null {
    let owner: CbarDateInputElement | null = null;

    document.querySelectorAll('cbar-date-input').forEach((node) => {
        if (owner || !isCbarDateInput(node) || !node.open) {
            return;
        }
        if (isPointerInsideOpenDateMenu(path, node)) {
            owner = node;
        }
    });

    return owner;
}

function findDateInputInComposedPath(path: EventTarget[]): CbarDateInputElement | null {
    for (const entry of path) {
        if (entry instanceof HTMLElement && entry.tagName === 'CBAR-DATE-INPUT' && isCbarDateInput(entry)) {
            return entry;
        }
    }

    return null;
}

function wireDatePopoverDismiss(): void {
    if (datePopoverDismissWired) {
        return;
    }

    datePopoverDismissWired = true;

    // Close any open calendar before hit-testing so a top-layer popover cannot
    // swallow the click meant for a sibling date field.
    document.addEventListener('pointerdown', (event) => {
        const path = event.composedPath();

        // Clicks on month/year (or any calendar chrome) belong to the open menu
        // even when that menu visually overlaps a sibling date field.
        const menuOwner = findOpenDateInputOwningPointer(path);
        if (menuOwner) {
            closeOpenDatePopoversExcept(menuOwner);
            return;
        }

        const pointerTarget = resolveTargetDateInputFromPointer(event.clientX, event.clientY);
        const pathInput = findDateInputInComposedPath(path);
        const activeInput = pointerTarget ?? pathInput;
        let closedSiblingForPointerTarget = false;

        document.querySelectorAll('cbar-date-input').forEach((node) => {
            if (!isCbarDateInput(node) || !node.open) {
                return;
            }

            if (activeInput && activeInput !== node) {
                if (pointerTarget === activeInput) {
                    closedSiblingForPointerTarget = true;
                }
                forceCloseDatePopover(node);
                return;
            }

            if (!activeInput) {
                forceCloseDatePopover(node);
            }
        });

        if (activeInput) {
            closeOpenDatePopoversExcept(activeInput);

            if (closedSiblingForPointerTarget) {
                requestAnimationFrame(() => {
                    activeInput.open = true;
                });
            }
        }
    }, true);

    document.addEventListener('focusin', (event) => {
        const path = event.composedPath();
        const menuOwner = findOpenDateInputOwningPointer(path);
        const activeInput = menuOwner ?? findDateInputInComposedPath(path);
        if (activeInput) {
            closeOpenDatePopoversExcept(activeInput);
        }
    }, true);
}

function ensureFilterDateInputMenuPosition(input: CbarDateInputElement): void {
    if (
        input.fixedMenuPosition
        || input.hasAttribute('fixed-menu-position')
        || input.hasAttribute('data-cbar-date-menu-position-set')
    ) {
        return;
    }

    const inFilterGrid = input.closest(
        'form.cbar-filter-form cbar-form-grid, form[name="filter_form"] cbar-form-grid'
    );
    if (!inFilterGrid) {
        return;
    }

    input.fixedMenuPosition = true;
    input.setAttribute('data-cbar-date-menu-position-set', '');
}

export function setupExclusiveDatePopovers(root: ParentNode = document): void {
    wireDatePopoverDismiss();

    const inputs = Array.from(root.querySelectorAll('cbar-date-input')).filter(isCbarDateInput);

    inputs.forEach((input) => {
        ensureFilterDateInputMenuPosition(input);

        if (input.hasAttribute('data-cbar-date-exclusive-wired')) {
            return;
        }

        input.setAttribute('data-cbar-date-exclusive-wired', '');
        input.addEventListener('pointerdown', () => {
            closeOpenDatePopoversExcept(input);
        }, true);
    });
}

export function createDateRangePickers(
    fromInput: CbarDateInputElement,
    toInput: CbarDateInputElement
): void {
    const sync = () => applyDateRangeConstraints(fromInput, toInput);

    fromInput.addEventListener('date-changed', sync);
    toInput.addEventListener('date-changed', sync);

    sync();
}

export async function setupCbarDateRangeById(
    fromId: string,
    toId: string
): Promise<void> {
    await customElements.whenDefined('cbar-date-input');

    const fromInput = document.getElementById(fromId) as CbarDateInputElement | null;
    const toInput = document.getElementById(toId) as CbarDateInputElement | null;

    if (!fromInput || !toInput) {
        return;
    }

    hydrateCbarDateInput(fromInput);
    hydrateCbarDateInput(toInput);
    setupExclusiveDatePopovers(fromInput.parentElement ?? document);
    createDateRangePickers(fromInput, toInput);
}
